import { ControlFacet } from '../models/controls/ControlFacet';
import { IItemCache } from '../models/IItemCacheObserver';

export interface IItemFilterProps {
  itemKey: string;
}

/**
 * Represents a filter to be applied to an item.
 */
export interface IItemFilter {
  (itemCache: IItemCache, props: IItemFilterProps): boolean;
}

/**
 * A handler to include each item if it matches the given filter predicate.
 * The key of the handler may be used by a control to 'pre-apply' the filter
 * when obtaining data from a data source.
 */
export interface IItemFilterHandler {
  /**
   * A unique key identifying this handler.
   * This will be used by control handler implementations to determine what the filter
   * is, by identity, in case it can be translated into a modification of the `itemKey`
   * and thus processed by the back-end, rather than evaluated client-side.
   */
  key: string;
  /**
   * The function to determine if an item matches the filter.
   */
  filter: IItemFilter;
}

/**
 * Control point which may be used to pre-filter an item
 * by returning an alternate item set with as many of the
 * given filter handlers incorporated into the key.
 */
export const filteredItemSetControl: ControlFacet<{
  /**
   * A set of filter handlers to apply to items.
   * These will be OR'd together, so they represent a union of item types allowed by the filters.
   */
  handlers: IItemFilterHandler[];
}, {
    /**
     * An override for the original item key, representing some of the filters 'baked in' to the key.
     * This will let a back-end make a filtered request, to avoid processing extra items client-side.
     */
    itemKey: string;
  }> = new ControlFacet('itemSetFilter');
